import { Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { useState } from 'react';
import StepExecutionLogsRenderer from '@/pages/Run/components/StepExecutionLogs/StepExecutionLogsRenderer.tsx';
import { getRenderableStepExecutionLogs } from '@/pages/Run/components/StepExecutionLogs/renderableLogs.ts';
import { LogSeverity, StepExecutionLogs } from '@/types/stepExecutions.ts';

const MAX_VISIBLE_ROUTINE_LOGS = 5;

type StepExecutionActivitySectionProps = {
  logs: StepExecutionLogs;
  isPending: boolean;
  showTitle: boolean;
};

const shouldAlwaysBeVisible = (log: StepExecutionLogs[number]) =>
  log.severity === LogSeverity.Warning ||
  log.severity === LogSeverity.Error ||
  Boolean(log.detail_message);

const StepExecutionActivitySection = ({
  logs,
  isPending,
  showTitle,
}: StepExecutionActivitySectionProps) => {
  const renderableLogs = getRenderableStepExecutionLogs(logs);
  const routineLogs = renderableLogs.filter((log) => !shouldAlwaysBeVisible(log));
  const isCollapsible = routineLogs.length > MAX_VISIBLE_ROUTINE_LOGS;
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasUserToggledExpansion, setHasUserToggledExpansion] = useState(false);
  const showAllRoutineLogs = !isCollapsible || (hasUserToggledExpansion && isExpanded);
  const visibleRoutineLogIds = new Set(
    showAllRoutineLogs
      ? routineLogs.map((log) => log.id)
      : routineLogs.slice(0, MAX_VISIBLE_ROUTINE_LOGS).map((log) => log.id),
  );
  const visibleLogs = renderableLogs.filter(
    (log) => shouldAlwaysBeVisible(log) || visibleRoutineLogIds.has(log.id),
  );
  const hiddenLogCount = routineLogs.length - MAX_VISIBLE_ROUTINE_LOGS;
  const toggleLabel = showAllRoutineLogs
    ? 'Show fewer activity entries'
    : `Show ${hiddenLogCount} more activity entr${hiddenLogCount === 1 ? 'y' : 'ies'}`;

  return (
    <Stack gap={5}>
      {showTitle && (
        <Text size="11px" fw={600} c="dimmed" tt="uppercase">
          Activity
        </Text>
      )}
      <StepExecutionLogsRenderer logs={visibleLogs} isPending={isPending} />
      {isCollapsible && (
        <UnstyledButton
          aria-expanded={showAllRoutineLogs}
          aria-label={toggleLabel}
          onClick={() => {
            setHasUserToggledExpansion(true);
            setIsExpanded((value) => !value);
          }}
          style={{
            alignItems: 'center',
            alignSelf: 'flex-start',
            color: '#6B7280',
            display: 'inline-flex',
            fontSize: '12px',
            fontWeight: 500,
            gap: 2,
            lineHeight: 1.2,
            marginLeft: '26px',
          }}
        >
          {toggleLabel}
          {showAllRoutineLogs ? <IconChevronUp size={13} /> : <IconChevronDown size={13} />}
        </UnstyledButton>
      )}
    </Stack>
  );
};

export default StepExecutionActivitySection;
